import React, { useState } from "react";
import {
  Dialog, Box, Typography, Stack, IconButton, Avatar, Tabs, Tab, Collapse,
} from "@mui/material";
import {
  Close, ShieldOutlined, CheckCircle, Cancel, BlockOutlined,
  DevicesOutlined, LocationOnOutlined, AccessTimeOutlined,
  LaptopOutlined, PhoneAndroidOutlined, TabletOutlined, FingerprintOutlined,
  SearchOutlined, AssignmentOutlined, ScheduleOutlined, GroupOutlined,
  ExpandMore, ExpandLess, VerifiedOutlined, HistoryOutlined, WifiOutlined,
  LogoutOutlined,
} from "@mui/icons-material";
import { deviceStore } from "../mock/deviceStore";

// ─── helpers ─────────────────────────────────────────────────────────────────

const PALETTE = ["#E86C00", "#2563EB", "#16A34A", "#9333EA", "#0891B2", "#DC2626", "#0D9488"];
const analystColor = (id) => PALETTE[parseInt(id || "0", 10) % PALETTE.length];
const initials = (name) =>
  (name || "?").split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

function devIcon(os) {
  if (!os) return LaptopOutlined;
  if (/ipad/i.test(os)) return TabletOutlined;
  if (/iphone|android/i.test(os)) return PhoneAndroidOutlined;
  return LaptopOutlined;
}
function devAccent(os) {
  if (!os) return "#2563EB";
  if (/ipad/i.test(os)) return "#9333EA";
  if (/iphone|android/i.test(os)) return "#16A34A";
  return "#2563EB";
}
function matchesSearch(item, q) {
  return [item.analystName, item.analystId, item.deviceName, item.macAddress, item.location, item.os]
    .join(" ").toLowerCase().includes(q);
}

// ─── STATUS BADGE ─────────────────────────────────────────────────────────────

const STATUS = {
  pending:  { label: "Pending",  fg: "#F59E0B", bg: "rgba(245,158,11,0.12)" },
  approved: { label: "Approved", fg: "#16A34A", bg: "rgba(22,163,74,0.12)"  },
  rejected: { label: "Rejected", fg: "#EF4444", bg: "rgba(239,68,68,0.12)"  },
  revoked:  { label: "Revoked",  fg: "#6B7280", bg: "rgba(107,114,128,0.12)"},
  active:   { label: "Active",   fg: "#16A34A", bg: "rgba(22,163,74,0.12)"  },
};

const StatusBadge = ({ status }) => {
  const s = STATUS[status] || STATUS.pending;
  return (
    <Box component="span" sx={{
      display: "inline-block", px: 1.2, py: "3px", borderRadius: "20px",
      fontSize: "11px", fontWeight: 700, lineHeight: 1.6,
      color: s.fg, backgroundColor: s.bg, border: `1px solid ${s.fg}30`,
      whiteSpace: "nowrap",
    }}>
      {s.label}
    </Box>
  );
};

// ─── STATS CARD (matches SelectTournament.StatsCard) ─────────────────────────

const StatsCard = ({ icon, value, label, subtitle, accent }) => (
  <Stack sx={{
    flex: "1 1 160px",
    p: { xs: 1.5, md: 2 },
    borderRadius: "12px",
    backgroundColor: "var(--surface-1)",
    border: "1px solid var(--border-1)",
    gap: 1,
    transition: "box-shadow 0.2s, border-color 0.2s",
    "&:hover": { boxShadow: "var(--shadow-1)", borderColor: accent || "var(--border-1)" },
  }}>
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
      <Box sx={{
        width: 40, height: 40, borderRadius: "10px",
        display: "flex", alignItems: "center", justifyContent: "center",
        backgroundColor: accent ? `${accent}15` : "var(--surface-2)",
        color: accent || "var(--text-2)",
      }}>
        {icon}
      </Box>
      <Typography sx={{ fontSize: { xs: "24px", md: "28px" }, fontWeight: 700, lineHeight: 1, color: "var(--text-1)" }}>
        {value ?? "--"}
      </Typography>
    </Stack>
    <Stack>
      <Typography sx={{ fontSize: { xs: "12px", md: "13px" }, fontWeight: 600, color: "var(--text-1)" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: { xs: "10px", md: "11px" }, color: "var(--text-2)", lineHeight: 1.3 }}>
        {subtitle}
      </Typography>
    </Stack>
  </Stack>
);

// ─── SEARCH BAR ───────────────────────────────────────────────────────────────

const SearchBar = ({ value, onChange }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{
    backgroundColor: "var(--surface-2)",
    border: "1px solid var(--border-1)",
    borderRadius: "10px", px: 1.5, mb: 2,
  }}>
    <SearchOutlined sx={{ fontSize: 18, color: "var(--text-2)", flexShrink: 0 }} />
    <Box
      component="input"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Search by name, device, MAC address, location…"
      sx={{
        flex: 1, border: "none", background: "transparent",
        color: "var(--text-1)", fontSize: "14px", py: "10px", outline: "none",
        "::placeholder": { color: "var(--text-2)", opacity: 0.6 },
      }}
    />
    {value && (
      <IconButton size="small" onClick={() => onChange("")} sx={{ color: "var(--text-2)", p: "2px" }}>
        <Close sx={{ fontSize: 14 }} />
      </IconButton>
    )}
  </Stack>
);

// ─── EMPTY STATE ──────────────────────────────────────────────────────────────

const Empty = ({ icon: Icon, title, sub }) => (
  <Box sx={{ textAlign: "center", py: 7 }}>
    <Box sx={{
      width: 60, height: 60, borderRadius: "14px",
      backgroundColor: "var(--surface-2)", border: "1px solid var(--border-1)",
      display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2,
    }}>
      <Icon sx={{ fontSize: 26, color: "var(--text-2)", opacity: 0.5 }} />
    </Box>
    <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "var(--text-1)", mb: 0.5 }}>{title}</Typography>
    {sub && <Typography sx={{ fontSize: "13px", color: "var(--text-2)" }}>{sub}</Typography>}
  </Box>
);

// ─── SECTION LABEL ────────────────────────────────────────────────────────────

const SectionLabel = ({ children, icon: Icon, iconColor }) => (
  <Stack direction="row" alignItems="center" spacing={0.8} mb={1.5}>
    {Icon && <Icon sx={{ fontSize: 14, color: iconColor || "var(--text-2)" }} />}
    <Typography sx={{
      fontSize: "11px", fontWeight: 700, color: "var(--text-2)",
      textTransform: "uppercase", letterSpacing: 0.7,
    }}>
      {children}
    </Typography>
  </Stack>
);

// ─── INLINE BUTTON ────────────────────────────────────────────────────────────

const InlineBtn = ({ onClick, color, outlined, children, small }) => (
  <Box
    component="button"
    onClick={onClick}
    sx={{
      px: small ? 1.2 : 1.5, py: small ? "5px" : "7px",
      borderRadius: "8px", cursor: "pointer",
      border: `1px solid ${outlined ? color + "50" : "transparent"}`,
      backgroundColor: outlined ? "transparent" : color,
      color: outlined ? color : "#fff",
      fontSize: "12px", fontWeight: 700,
      display: "flex", alignItems: "center", gap: "4px", flexShrink: 0,
      transition: "opacity 0.15s, background-color 0.15s",
      "&:hover": outlined
        ? { backgroundColor: color + "10" }
        : { opacity: 0.85 },
    }}
  >
    {children}
  </Box>
);

// ─── SESSION HISTORY DIALOG ───────────────────────────────────────────────────

const SessionHistoryDialog = ({ open, device, sessions, onClose, onRevoke }) => {
  if (!device) return null;
  const DevIcon = devIcon(device.os);
  const dc = devAccent(device.os);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: 1600 }}
      PaperProps={{
        sx: {
          backgroundColor: "var(--surface-1)",
          backgroundImage: "none",
          borderRadius: "16px",
          border: "1px solid var(--border-1)",
          overflow: "hidden",
        },
      }}
    >
      {/* Device header */}
      <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: "1px solid var(--border-1)" }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={2}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box sx={{
              width: 46, height: 46, borderRadius: "12px", flexShrink: 0,
              backgroundColor: `${dc}15`, border: `1px solid ${dc}22`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <DevIcon sx={{ fontSize: 24, color: dc }} />
            </Box>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" mb={0.3}>
                <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "var(--text-1)" }}>
                  {device.deviceName}
                </Typography>
                <StatusBadge status={device.status} />
              </Stack>
              <Typography sx={{ fontSize: "12px", color: "var(--text-2)", fontFamily: "monospace" }}>
                {device.macAddress}
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "var(--text-2)" }}>
                {device.analystName} (ID: {device.analystId}) · {device.location}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small" sx={{ color: "var(--text-2)", ml: 1 }}>
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>

        {/* Device meta */}
        <Box sx={{
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.2,
          p: 1.5, borderRadius: "10px",
          backgroundColor: "var(--surface-2)", border: "1px solid var(--border-1)",
          mb: device.status === "active" ? 1.5 : 0,
        }}>
          {[
            { label: "Last Login",  value: device.lastLogin ? deviceStore.fmtDate(device.lastLogin) : "Never" },
            { label: "Approved On", value: deviceStore.fmtDate(device.approvedAt) },
          ].map(({ label, value }) => (
            <Box key={label}>
              <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "var(--text-2)", textTransform: "uppercase", letterSpacing: 0.5, mb: "2px" }}>
                {label}
              </Typography>
              <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "var(--text-1)" }}>
                {value}
              </Typography>
            </Box>
          ))}
        </Box>

        {device.status === "active" && (
          <Box
            component="button"
            onClick={() => { onRevoke(device.id); onClose(); }}
            sx={{
              width: "100%", py: "9px", borderRadius: "9px",
              border: "1px solid rgba(239,68,68,0.35)", cursor: "pointer",
              backgroundColor: "transparent", color: "#EF4444",
              fontSize: "13px", fontWeight: 700,
              display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
              "&:hover": { backgroundColor: "rgba(239,68,68,0.07)" }, transition: "0.15s",
            }}
          >
            <BlockOutlined sx={{ fontSize: 16 }} /> Revoke Device Access
          </Box>
        )}
      </Box>

      {/* Session list */}
      <Box sx={{ maxHeight: "55vh", overflowY: "auto", px: 3, py: 2.5 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <HistoryOutlined sx={{ fontSize: 16, color: "var(--text-2)" }} />
          <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "var(--text-2)", textTransform: "uppercase", letterSpacing: 0.7 }}>
            Session History · {sessions.length} session{sessions.length !== 1 ? "s" : ""}
          </Typography>
        </Stack>

        {sessions.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <Typography sx={{ fontSize: "13px", color: "var(--text-2)" }}>No sessions recorded yet</Typography>
          </Box>
        ) : (
          <Stack spacing={1}>
            {sessions.map((session, i) => {
              const isActive = !session.logoutAt;
              return (
                <Box
                  key={session.id}
                  sx={{
                    borderRadius: "10px", p: 1.5,
                    backgroundColor: "var(--surface-2)",
                    border: "1px solid var(--border-1)",
                    borderLeft: `3px solid ${isActive ? "#16A34A" : "var(--border-1)"}`,
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.8}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{
                        width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                        backgroundColor: isActive ? "#16A34A" : "var(--text-2)",
                        opacity: isActive ? 1 : 0.4,
                      }} />
                      <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "var(--text-1)" }}>
                        {isActive ? "Active Session" : `Session #${sessions.length - i}`}
                      </Typography>
                    </Stack>
                    <Typography sx={{
                      fontSize: "12px", fontWeight: 700,
                      color: isActive ? "#16A34A" : "var(--text-2)",
                    }}>
                      {isActive ? "● Live" : session.duration}
                    </Typography>
                  </Stack>

                  <Stack spacing={0.4}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0.4, sm: 2 }}>
                      <Stack direction="row" spacing={0.6} alignItems="center">
                        <AccessTimeOutlined sx={{ fontSize: 12, color: "var(--text-2)", flexShrink: 0 }} />
                        <Typography sx={{ fontSize: "12px", color: "var(--text-2)" }}>
                          Login: {deviceStore.fmtDate(session.loginAt)}
                        </Typography>
                      </Stack>
                      {session.logoutAt && (
                        <Stack direction="row" spacing={0.6} alignItems="center">
                          <LogoutOutlined sx={{ fontSize: 12, color: "var(--text-2)", flexShrink: 0 }} />
                          <Typography sx={{ fontSize: "12px", color: "var(--text-2)" }}>
                            Logout: {deviceStore.fmtDate(session.logoutAt)}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      <Stack direction="row" spacing={0.6} alignItems="center">
                        <LocationOnOutlined sx={{ fontSize: 12, color: "var(--text-2)", flexShrink: 0 }} />
                        <Typography sx={{ fontSize: "12px", color: "var(--text-2)" }}>
                          {session.location}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.6} alignItems="center">
                        <WifiOutlined sx={{ fontSize: 12, color: "var(--text-2)", flexShrink: 0 }} />
                        <Typography sx={{ fontSize: "12px", color: "var(--text-2)", fontFamily: "monospace" }}>
                          {session.ip}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </Dialog>
  );
};

// ─── REQUEST CARD ─────────────────────────────────────────────────────────────

const RequestCard = ({ req, onAction, dimmed }) => {
  const [loading, setLoading] = useState(null);
  const ac = analystColor(req.analystId);
  const DevIcon = devIcon(req.os);
  const dc = devAccent(req.os);

  const act = (action) => { setLoading(action); onAction(action, req.id); };

  return (
    <Box sx={{
      borderRadius: "12px", overflow: "hidden", mb: 1.5,
      backgroundColor: "var(--surface-1)", border: "1px solid var(--border-1)",
      opacity: dimmed ? 0.6 : 1,
      boxShadow: dimmed ? "none" : "0 1px 4px rgba(0,0,0,0.06)",
      transition: "box-shadow 0.2s, border-color 0.2s",
      "&:hover": dimmed ? {} : { boxShadow: "var(--shadow-1)", borderColor: `${ac}44` },
    }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{
        px: 2, py: 1.5,
        borderBottom: "1px solid var(--border-1)",
        backgroundColor: dimmed ? "var(--surface-2)" : `${ac}09`,
      }}>
        <Avatar sx={{ width: 36, height: 36, fontSize: "13px", fontWeight: 700, flexShrink: 0, backgroundColor: `${ac}1A`, color: ac }}>
          {initials(req.analystName)}
        </Avatar>
        <Box flex={1} minWidth={0}>
          <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "var(--text-1)", lineHeight: 1.3 }}>
            {req.analystName}
          </Typography>
          <Typography sx={{ fontSize: "11px", color: "var(--text-2)" }}>
            ID: {req.analystId} · {deviceStore.fmtDate(req.requestedAt)}
          </Typography>
        </Box>
        <StatusBadge status={req.status} />
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ px: 2, py: 1.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start" flex={1}>
          <Box sx={{
            width: 40, height: 40, borderRadius: "10px", flexShrink: 0,
            backgroundColor: `${dc}15`, border: `1px solid ${dc}22`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <DevIcon sx={{ fontSize: 22, color: dc }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "var(--text-1)" }}>
              {req.deviceName}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center" mt="2px">
              <FingerprintOutlined sx={{ fontSize: 12, color: "var(--text-2)" }} />
              <Typography sx={{ fontSize: "12px", color: "var(--text-2)", fontFamily: "monospace" }}>
                {req.macAddress}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center" mt="2px">
              <LocationOnOutlined sx={{ fontSize: 12, color: "var(--text-2)" }} />
              <Typography sx={{ fontSize: "12px", color: "var(--text-2)" }}>{req.location}</Typography>
            </Stack>
            {req.approvedAt && (
              <Stack direction="row" spacing={0.5} alignItems="center" mt="2px">
                <VerifiedOutlined sx={{ fontSize: 12, color: "var(--text-2)" }} />
                <Typography sx={{ fontSize: "12px", color: "var(--text-2)" }}>
                  Processed: {deviceStore.fmtDate(req.approvedAt)}
                </Typography>
              </Stack>
            )}
          </Box>
        </Stack>
      </Stack>

      {!dimmed && req.status === "pending" && (
        <Stack direction="row" sx={{ borderTop: "1px solid var(--border-1)" }}>
          {[
            { action: "approve", label: "Approve", loadLabel: "Approving…", Icon: CheckCircle, bg: "#16A34A", hov: "#15803D" },
            { action: "reject",  label: "Reject",  loadLabel: "Rejecting…",  Icon: Cancel,      bg: "#EF4444", hov: "#DC2626" },
          ].map(({ action, label, loadLabel, Icon, bg, hov }, i) => (
            <React.Fragment key={action}>
              {i > 0 && <Box sx={{ width: "1px", backgroundColor: "var(--border-1)" }} />}
              <Box
                component="button"
                onClick={() => act(action)}
                disabled={!!loading}
                sx={{
                  flex: 1, py: "11px", border: "none", cursor: "pointer",
                  backgroundColor: bg, color: "#fff", fontSize: "13px", fontWeight: 700,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                  transition: "background-color 0.15s",
                  "&:hover": { backgroundColor: hov },
                  "&:disabled": { opacity: 0.7, cursor: "default" },
                }}
              >
                <Icon sx={{ fontSize: 16 }} />
                {loading === action ? loadLabel : label}
              </Box>
            </React.Fragment>
          ))}
        </Stack>
      )}
    </Box>
  );
};

// ─── DEVICE ROW (inside Analyst card) ────────────────────────────────────────

const DeviceRow = ({ device, onRevoke, onViewHistory }) => {
  const DevIcon = devIcon(device.os);
  const dc = devAccent(device.os);
  return (
    <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{
      p: 1.5, borderRadius: "10px",
      backgroundColor: "var(--surface-2)", border: "1px solid var(--border-1)",
    }}>
      <Box sx={{
        width: 36, height: 36, borderRadius: "8px", flexShrink: 0,
        backgroundColor: `${dc}15`, border: `1px solid ${dc}22`,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <DevIcon sx={{ fontSize: 18, color: dc }} />
      </Box>
      <Box flex={1} minWidth={0}>
        <Stack direction="row" alignItems="center" spacing={1} mb="2px" flexWrap="wrap">
          <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "var(--text-1)" }}>
            {device.deviceName}
          </Typography>
          <StatusBadge status={device.status} />
        </Stack>
        <Typography sx={{ fontSize: "11px", color: "var(--text-2)", fontFamily: "monospace" }}>
          {device.macAddress}
        </Typography>
        <Typography sx={{ fontSize: "11px", color: "var(--text-2)", mt: "1px" }}>
          Last login: {device.lastLogin ? deviceStore.fmtDate(device.lastLogin) : "Never"}
          {" · "}{device.location}
        </Typography>
      </Box>
      {/* Action buttons */}
      <Stack direction="row" spacing={0.8} flexShrink={0} alignItems="center">
        <InlineBtn small outlined color="#0891B2" onClick={() => onViewHistory(device.id)}>
          <HistoryOutlined sx={{ fontSize: 14 }} /> View
        </InlineBtn>
        {device.status === "active" && (
          <InlineBtn small outlined color="#EF4444" onClick={() => onRevoke(device.id)}>
            <BlockOutlined sx={{ fontSize: 14 }} /> Revoke
          </InlineBtn>
        )}
      </Stack>
    </Stack>
  );
};

// ─── ANALYST CARD ────────────────────────────────────────────────────────────

const AnalystCard = ({ group, onRevoke, onViewHistory }) => {
  const { analystId, analystName, devices } = group;
  const ac = analystColor(analystId);
  const active = devices.filter((d) => d.status === "active").length;
  const lastLogin = devices.reduce((best, d) => {
    if (!d.lastLogin) return best;
    return !best || d.lastLogin > best ? d.lastLogin : best;
  }, null);

  return (
    <Box sx={{
      borderRadius: "12px", overflow: "hidden", mb: 1.5,
      backgroundColor: "var(--surface-1)", border: "1px solid var(--border-1)",
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      transition: "box-shadow 0.2s, border-color 0.2s",
      "&:hover": { boxShadow: "var(--shadow-1)", borderColor: `${ac}44` },
    }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{
        px: 2, py: 1.5,
        borderBottom: "1px solid var(--border-1)",
        backgroundColor: `${ac}09`,
      }}>
        <Avatar sx={{ width: 40, height: 40, fontSize: "14px", fontWeight: 700, flexShrink: 0, backgroundColor: `${ac}1A`, color: ac }}>
          {initials(analystName)}
        </Avatar>
        <Box flex={1} minWidth={0}>
          <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "var(--text-1)" }}>
            {analystName}
          </Typography>
          <Typography sx={{ fontSize: "11px", color: "var(--text-2)" }}>
            ID: {analystId}
            {lastLogin ? ` · Last login: ${deviceStore.fmtDate(lastLogin)}` : " · Never logged in"}
          </Typography>
        </Box>
        <Box sx={{
          px: 1.5, py: "4px", borderRadius: "20px",
          backgroundColor: active > 0 ? "rgba(22,163,74,0.10)" : "var(--surface-2)",
          border: `1px solid ${active > 0 ? "rgba(22,163,74,0.25)" : "var(--border-1)"}`,
        }}>
          <Typography sx={{ fontSize: "11px", fontWeight: 700, color: active > 0 ? "#16A34A" : "var(--text-2)" }}>
            {active}/{devices.length} active
          </Typography>
        </Box>
      </Stack>
      <Stack spacing={1} sx={{ p: 1.5 }}>
        {devices.map((dev) => (
          <DeviceRow key={dev.id} device={dev} onRevoke={onRevoke} onViewHistory={onViewHistory} />
        ))}
      </Stack>
    </Box>
  );
};

// ─── DEVICE CARD (Devices tab) ────────────────────────────────────────────────

const DeviceCard = ({ device, onRevoke, onViewHistory }) => {
  const DevIcon = devIcon(device.os);
  const dc = devAccent(device.os);
  const ac = analystColor(device.analystId);
  return (
    <Box sx={{
      borderRadius: "12px", overflow: "hidden",
      backgroundColor: "var(--surface-1)", border: "1px solid var(--border-1)",
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      transition: "box-shadow 0.2s, border-color 0.2s",
      "&:hover": { boxShadow: "var(--shadow-1)", borderColor: `${dc}44` },
    }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{
        px: 2, py: 1.5,
        borderBottom: "1px solid var(--border-1)",
        backgroundColor: `${dc}08`,
      }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: "10px", flexShrink: 0,
          backgroundColor: `${dc}15`, border: `1px solid ${dc}22`,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <DevIcon sx={{ fontSize: 22, color: dc }} />
        </Box>
        <Box flex={1} minWidth={0}>
          <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "var(--text-1)", lineHeight: 1.3 }}>
            {device.deviceName}
          </Typography>
          <Stack direction="row" spacing={0.8} alignItems="center">
            <Avatar sx={{ width: 16, height: 16, fontSize: "8px", fontWeight: 700, backgroundColor: `${ac}1A`, color: ac }}>
              {initials(device.analystName)}
            </Avatar>
            <Typography sx={{ fontSize: "12px", color: "var(--text-2)" }}>
              {device.analystName} (ID: {device.analystId})
            </Typography>
          </Stack>
        </Box>
        <StatusBadge status={device.status} />
      </Stack>

      <Box sx={{ px: 2, py: 1.5 }}>
        <Box sx={{
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5,
          p: 1.5, borderRadius: "10px",
          backgroundColor: "var(--surface-2)", border: "1px solid var(--border-1)",
          mb: 1.5,
        }}>
          {[
            { label: "MAC Address", value: device.macAddress, mono: true },
            { label: "Location",    value: device.location },
            { label: "Last Login",  value: device.lastLogin ? deviceStore.fmtDate(device.lastLogin) : "Never" },
            { label: "Approved On", value: deviceStore.fmtDate(device.approvedAt) },
          ].map(({ label, value, mono }) => (
            <Box key={label}>
              <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "var(--text-2)", textTransform: "uppercase", letterSpacing: 0.5, mb: "2px" }}>
                {label}
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "var(--text-1)", fontWeight: 500, fontFamily: mono ? "monospace" : undefined, wordBreak: "break-all" }}>
                {value}
              </Typography>
            </Box>
          ))}
        </Box>

        <Stack direction="row" spacing={1}>
          <Box
            component="button"
            onClick={() => onViewHistory(device.id)}
            sx={{
              flex: 1, py: "9px", borderRadius: "9px",
              border: "1px solid rgba(8,145,178,0.35)", cursor: "pointer",
              backgroundColor: "transparent", color: "#0891B2",
              fontSize: "13px", fontWeight: 700,
              display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
              "&:hover": { backgroundColor: "rgba(8,145,178,0.07)" }, transition: "0.15s",
            }}
          >
            <HistoryOutlined sx={{ fontSize: 16 }} /> View Sessions
          </Box>
          {device.status === "active" && (
            <Box
              component="button"
              onClick={() => onRevoke(device.id)}
              sx={{
                flex: 1, py: "9px", borderRadius: "9px",
                border: "1px solid rgba(239,68,68,0.35)", cursor: "pointer",
                backgroundColor: "transparent", color: "#EF4444",
                fontSize: "13px", fontWeight: 700,
                display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                "&:hover": { backgroundColor: "rgba(239,68,68,0.07)" }, transition: "0.15s",
              }}
            >
              <BlockOutlined sx={{ fontSize: 16 }} /> Revoke Access
            </Box>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

// ─── MAIN PANEL ───────────────────────────────────────────────────────────────

const DeviceAccessPanel = ({ open, onClose }) => {
  const [tab, setTab] = useState(0);
  const [tick, setTick] = useState(0);
  const [search, setSearch] = useState("");
  const [dismissed, setDismissed] = useState(new Set());
  const [showHistory, setShowHistory] = useState(false);
  const [sessionDeviceId, setSessionDeviceId] = useState(null);

  const refresh = () => setTick((t) => t + 1);

  const handleRequestAction = (action, id) => {
    setDismissed((prev) => { const s = new Set(prev); s.add(id); return s; });
    if (action === "approve") deviceStore.approveRequest(id);
    else deviceStore.rejectRequest(id);
    refresh();
  };

  const handleRevokeDevice = (deviceId) => {
    deviceStore.revokeDevice(deviceId);
    refresh();
  };

  const openSessions = (deviceId) => setSessionDeviceId(deviceId);
  const closeSessions = () => setSessionDeviceId(null);

  const allRequests = deviceStore.getAllRequests();
  const allDevices = deviceStore.getAllDevices();
  const q = search.trim().toLowerCase();

  const pending = allRequests.filter((r) => r.status === "pending" && !dismissed.has(r.id));
  const history = allRequests.filter((r) => r.status !== "pending" || dismissed.has(r.id));
  const filteredPending = q ? pending.filter((r) => matchesSearch(r, q)) : pending;
  const filteredHistory = q ? history.filter((r) => matchesSearch(r, q)) : history;

  const analystMap = {};
  allDevices.forEach((d) => {
    if (!analystMap[d.analystId])
      analystMap[d.analystId] = { analystId: d.analystId, analystName: d.analystName, devices: [] };
    analystMap[d.analystId].devices.push(d);
  });
  const analystGroups = Object.values(analystMap).filter((g) =>
    !q || g.analystName.toLowerCase().includes(q) || g.analystId.includes(q) ||
    g.devices.some((d) => matchesSearch(d, q))
  );
  const filteredDevices = q ? allDevices.filter((d) => matchesSearch(d, q)) : allDevices;
  const activeCount = allDevices.filter((d) => d.status === "active").length;

  const tabLabels = [
    `Requests${pending.length ? ` (${pending.length})` : ""}`,
    `Analysts (${Object.keys(analystMap).length})`,
    `Devices (${allDevices.length})`,
  ];

  const sessionDevice = sessionDeviceId ? allDevices.find((d) => d.id === sessionDeviceId) : null;
  const sessionList = sessionDeviceId ? deviceStore.getSessionsForDevice(sessionDeviceId) : [];

  return (
    <>
      <Dialog
        fullScreen
        open={open}
        onClose={onClose}
        sx={{ zIndex: 1500 }}
        PaperProps={{
          sx: {
            backgroundColor: "var(--app-bg)", backgroundImage: "none",
            display: "flex", flexDirection: "column", overflow: "hidden",
          },
        }}
      >
        {/* ── Header ── */}
        <Box sx={{
          backgroundColor: "var(--surface-1)", borderBottom: "1px solid var(--border-1)",
          px: { xs: 2, md: 3 }, flexShrink: 0,
        }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ height: 56 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{
                width: 36, height: 36, borderRadius: "9px",
                backgroundColor: "rgba(232,108,0,0.12)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <ShieldOutlined sx={{ fontSize: 20, color: "rgba(232,108,0,1)" }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "var(--text-1)", lineHeight: 1.2 }}>
                  Device Access Control
                </Typography>
                <Typography sx={{ fontSize: "11px", color: "var(--text-2)" }}>
                  Manage analyst device registrations
                </Typography>
              </Box>
            </Stack>
            <IconButton onClick={onClose} sx={{ color: "var(--text-2)", borderRadius: "8px", "&:hover": { backgroundColor: "var(--surface-2)" } }}>
              <Close sx={{ fontSize: 22 }} />
            </IconButton>
          </Stack>

          <Tabs
            value={tab}
            onChange={(_, v) => { setTab(v); setSearch(""); setShowHistory(false); }}
            sx={{
              minHeight: 40,
              "& .MuiTab-root": { minHeight: 40, fontSize: "13px", fontWeight: 600, textTransform: "none", color: "var(--text-2)", px: { xs: 1.5, sm: 2 } },
              "& .Mui-selected": { color: "rgba(232,108,0,1) !important" },
              "& .MuiTabs-indicator": { backgroundColor: "rgba(232,108,0,1)" },
            }}
          >
            {tabLabels.map((l) => <Tab key={l} label={l} />)}
          </Tabs>
        </Box>

        {/* ── Stats strip ── */}
        <Box sx={{
          px: { xs: 2, md: 3 }, py: 2, flexShrink: 0,
          borderBottom: "1px solid var(--border-1)",
          backgroundColor: "var(--app-bg)",
        }}>
          <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1.5 }}>
            <StatsCard icon={<AssignmentOutlined sx={{ fontSize: 22 }} />} value={allRequests.length} label="Total Requests" subtitle="All time submissions" accent="#6366F1" />
            <StatsCard icon={<ScheduleOutlined sx={{ fontSize: 22 }} />} value={pending.length} label="Pending" subtitle="Awaiting review" accent="#F59E0B" />
            <StatsCard icon={<DevicesOutlined sx={{ fontSize: 22 }} />} value={allDevices.length} label="Devices" subtitle="Registered total" accent="#0891B2" />
            <StatsCard icon={<CheckCircle sx={{ fontSize: 22 }} />} value={activeCount} label="Active" subtitle="Currently active" accent="#16A34A" />
          </Stack>
        </Box>

        {/* ── Scrollable content ── */}
        <Box sx={{ flex: 1, overflowY: "auto", px: { xs: 2, md: 3 }, py: 2 }}>
          <Box sx={{ maxWidth: 860, mx: "auto" }}>
            <SearchBar value={search} onChange={setSearch} />

            {/* Requests tab */}
            {tab === 0 && (
              <>
                {filteredPending.length > 0 && (
                  <>
                    <SectionLabel icon={ScheduleOutlined} iconColor="#F59E0B">Pending — {filteredPending.length}</SectionLabel>
                    {filteredPending.map((r) => <RequestCard key={r.id} req={r} onAction={handleRequestAction} dimmed={false} />)}
                  </>
                )}
                {filteredPending.length === 0 && !q && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, backgroundColor: "rgba(22,163,74,0.07)", border: "1px solid rgba(22,163,74,0.2)", borderRadius: "10px", px: 2, py: 1.5, mb: 2 }}>
                    <CheckCircle sx={{ fontSize: 18, color: "#16A34A" }} />
                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#16A34A" }}>All clear — no pending requests.</Typography>
                  </Box>
                )}
                {filteredHistory.length > 0 && (
                  <Box mt={filteredPending.length > 0 ? 3 : 0}>
                    <Box component="button" onClick={() => setShowHistory((v) => !v)} sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", backgroundColor: "var(--surface-2)", border: "1px solid var(--border-1)", borderRadius: "10px", px: 2, py: "10px", cursor: "pointer", mb: 1.5, color: "var(--text-2)", transition: "border-color 0.15s", "&:hover": { borderColor: "var(--text-2)" } }}>
                      <Stack direction="row" spacing={0.8} alignItems="center">
                        <AssignmentOutlined sx={{ fontSize: 14 }} />
                        <Typography sx={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.7 }}>History — {filteredHistory.length}</Typography>
                      </Stack>
                      {showHistory ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
                    </Box>
                    <Collapse in={showHistory}>
                      {filteredHistory.map((r) => <RequestCard key={r.id} req={r} onAction={handleRequestAction} dimmed />)}
                    </Collapse>
                  </Box>
                )}
                {allRequests.length === 0 && <Empty icon={AssignmentOutlined} title="No device requests yet" sub="Requests will appear here when analysts submit them." />}
                {q && filteredPending.length === 0 && filteredHistory.length === 0 && <Empty icon={SearchOutlined} title={`No results for "${search}"`} sub="Try a different name, device, or MAC address." />}
              </>
            )}

            {/* Analysts tab */}
            {tab === 1 && (
              <>
                {analystGroups.length === 0
                  ? q ? <Empty icon={SearchOutlined} title={`No results for "${search}"`} /> : <Empty icon={GroupOutlined} title="No analysts with registered devices" sub="Devices appear here once requests are approved." />
                  : analystGroups.map((g) => <AnalystCard key={`${g.analystId}-${tick}`} group={g} onRevoke={handleRevokeDevice} onViewHistory={openSessions} />)
                }
              </>
            )}

            {/* Devices tab */}
            {tab === 2 && (
              <>
                {filteredDevices.length === 0
                  ? q ? <Empty icon={SearchOutlined} title={`No results for "${search}"`} /> : <Empty icon={DevicesOutlined} title="No registered devices" />
                  : (
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                      {filteredDevices.map((dev) => <DeviceCard key={`${dev.id}-${tick}`} device={dev} onRevoke={handleRevokeDevice} onViewHistory={openSessions} />)}
                    </Box>
                  )
                }
              </>
            )}
          </Box>
        </Box>

        {/* ── Footer ── */}
        <Box sx={{ px: 3, py: 1.2, flexShrink: 0, borderTop: "1px solid var(--border-1)", backgroundColor: "var(--surface-1)" }}>
          <Typography sx={{ fontSize: "11px", color: "var(--text-2)", textAlign: "center" }}>
            Prototype mode — data resets on page refresh
          </Typography>
        </Box>
      </Dialog>

      {/* Session history dialog — stacked above main panel */}
      <SessionHistoryDialog
        open={!!sessionDeviceId}
        device={sessionDevice}
        sessions={sessionList}
        onClose={closeSessions}
        onRevoke={(id) => { handleRevokeDevice(id); closeSessions(); }}
      />
    </>
  );
};

export default DeviceAccessPanel;
